import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { SWEET_ALERT_TYPES } from '@app/constants';
import { doGetSchoolInfo } from '@app/redux/others/sagaActions';
import OtherSelector from '@app/redux/others/selector';
import SchoolSelector from '@app/redux/school/school.selector';
import {
    settingSchoolActions,
    settingSchoolSelector,
} from '@app/redux/settingSchool/settingSchool.slice';
import _kidGroupApis from '@app/apis/foodkid/kidGroup.api';
import { swal } from '@app/utils/alert';
import { MergedProperty } from '../types';

/**
 * Hook gom toàn bộ logic fetch data + selectors + actions cho SettingSchool
 */
export function useSchoolSettings() {
    const dispatch = useDispatch();

    // --- Selectors ---
    const schoolPropertyList = useSelector(
        settingSchoolSelector.schoolProperty,
    );
    const schoolPropertySettingList = useSelector(
        settingSchoolSelector.schoolPropertySetting,
    );
    const saveCount = useSelector(settingSchoolSelector.saveCount);
    const proPackagingCache = useSelector(OtherSelector.packaging) || [];
    const invoiceForm = useSelector(OtherSelector.invoiceForm) || [];
    const currentSchool = useSelector(SchoolSelector.currentSchool);

    // --- Local state ---
    const [kidGroupList, setKidGroupList] = useState<any[]>([]);

    // --- Effects ---
    useEffect(() => {
        dispatch(settingSchoolActions.doGetSchoolProperty());
    }, [dispatch]);

    useEffect(() => {
        dispatch(settingSchoolActions.doGetPropertySetting(currentSchool));
        dispatch(doGetSchoolInfo(currentSchool));
    }, [currentSchool, saveCount, dispatch]);

    useEffect(() => {
        if (currentSchool) {
            _kidGroupApis
                .getListByOId(currentSchool)
                .then(({ status, data }: any) => {
                    if (status === 200) {
                        setKidGroupList(data.data);
                        return;
                    }
                    setKidGroupList([]);
                });
        }
    }, [currentSchool]);

    // --- Merged settings (property + setting) ---
    const mergedSettings: MergedProperty[] =
        schoolPropertyList.length > 0
            ? _.sortBy(
                  schoolPropertySettingList
                      .filter((x: any) =>
                          schoolPropertyList
                              .map((p: any) => p.p_id)
                              .includes(x.p_id),
                      )
                      .map((item: any) => {
                          const prop = schoolPropertyList.find(
                              (x: any) => x.p_id === item.p_id,
                          );
                          if (prop) {
                              return {
                                  ...item,
                                  p_name: prop.p_name,
                                  p_sortorder: prop.p_sortorder,
                                  p_datatype: prop.p_datatype,
                                  p_group: prop.p_group,
                              };
                          }
                          return item;
                      }),
                  ['p_sortorder'],
              )
            : [];

    // --- Actions ---
    const onSave = useCallback(() => {
        const data = schoolPropertySettingList.filter(
            (x: any) => x.isEdit === true,
        );
        swal(
            SWEET_ALERT_TYPES.QUESTION,
            '<p> Bạn có thực sự muốn chỉnh sửa dữ liệu này không?</p>',
            true,
        ).then((res: any) => {
            if (res.value) {
                dispatch(settingSchoolActions.doSavePropertySetting(data));
            }
        });
    }, [schoolPropertySettingList, dispatch]);

    const onChangeData = useCallback(
        (value: any, code: string) => {
            dispatch(
                settingSchoolActions.doChangeValuePropertySetting({
                    code,
                    value,
                }),
            );
        },
        [dispatch],
    );

    return {
        mergedSettings,
        kidGroupList,
        proPackagingCache,
        invoiceForm,
        schoolPropertyList,
        onSave,
        onChangeData,
    };
}
